/**
 * R3's orchestration: block a fan and clear their pending message request in
 * the same action.
 *
 * The block is never rolled back when clearing the request fails. Blocking is
 * what the fan confirmed; a bookkeeping call failing afterward does not un-ask
 * for the thing they already confirmed. The fan is told through the shared
 * error modal instead, so the failure is never silently skipped.
 *
 * The message-request LIST read goes through `useCallSdk()` imperatively
 * rather than `useMessageRequestList`, so a component that merely imports this
 * hook does not mount an always-polling query.
 *
 * The DECLINE goes through the generated `useMessageRequestDecline` mutation
 * hook instead (ISSUE-798). A raw `callSdk().postMessageRequestDecline` never
 * reaches the generated `invalidateRelated` step, so the cached
 * `message-request/list` query was left untouched on this path and the requests
 * inbox could keep showing a request this very block had already cleared, for
 * the whole freshness window. A mutation hook subscribes to nothing and fires
 * nothing until it is called, so adopting it does not reintroduce the
 * always-polling query the list read above is avoiding.
 */
import {
  useCallSdk,
  useMessageRequestDecline,
} from "@sportsmet/messaging-sdk/private";
import { useBlockAdd } from "@sportsmet/profile-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { buildBlockConfirm } from "./moderationCopy";
import {
  findPendingRequestForSender,
  isFailedSdkResult,
  sdkErrorCode,
} from "./moderationRules";

export type typeBlockOutcome = {
  didBlock: boolean;
  didClearRequest: boolean;
  failedStep: "canceled" | "block" | "requestList" | "decline" | null;
  errorCode: string | null;
};

export function useBlockAndClearRequest(): {
  blockAndClear: (payload: { accountId: string }) => Promise<typeBlockOutcome>;
  isPending: boolean;
} {
  const blockAdd = useBlockAdd();
  const { callSdk } = useCallSdk();
  const requestDecline = useMessageRequestDecline();

  const blockAndClear = async (payload: {
    accountId: string;
  }): Promise<typeBlockOutcome> => {
    try {
      const blockResult = await blockAdd.mutateAsync({
        blockedAccountId: payload.accountId,
      });
      if (isFailedSdkResult(blockResult)) {
        showApiError(blockResult);
        return {
          didBlock: false,
          didClearRequest: false,
          failedStep: "block",
          errorCode: sdkErrorCode(blockResult),
        };
      }

      const requestListResult = await callSdk().postMessageRequestList({});
      if (isFailedSdkResult(requestListResult)) {
        showApiError(requestListResult);
        return {
          didBlock: true,
          didClearRequest: false,
          failedStep: "requestList",
          errorCode: sdkErrorCode(requestListResult),
        };
      }

      const pending = findPendingRequestForSender({
        items: requestListResult.data?.items ?? [],
        senderAccountId: payload.accountId,
      });
      if (pending === null) {
        return {
          didBlock: true,
          didClearRequest: true,
          failedStep: null,
          errorCode: null,
        };
      }

      const declineResult = await requestDecline.mutateAsync({
        chatId: pending.chatId,
      });
      if (isFailedSdkResult(declineResult)) {
        showApiError(declineResult);
        return {
          didBlock: true,
          didClearRequest: false,
          failedStep: "decline",
          errorCode: sdkErrorCode(declineResult),
        };
      }

      return {
        didBlock: true,
        didClearRequest: true,
        failedStep: null,
        errorCode: null,
      };
    } catch {
      showOfflineError();
      return {
        didBlock: false,
        didClearRequest: false,
        failedStep: "block",
        errorCode: null,
      };
    }
  };

  return { blockAndClear, isPending: blockAdd.isPending };
}

export function useBlockFan(): {
  blockFan: (payload: {
    accountId: string;
    displayName: string;
  }) => Promise<typeBlockOutcome>;
  isPending: boolean;
} {
  const { blockAndClear, isPending } = useBlockAndClearRequest();

  const blockFan = async (payload: {
    accountId: string;
    displayName: string;
  }): Promise<typeBlockOutcome> => {
    const confirmed = await confirmDestructive(
      buildBlockConfirm({ displayName: payload.displayName }),
    );
    if (!confirmed) {
      return {
        didBlock: false,
        didClearRequest: false,
        failedStep: "canceled",
        errorCode: null,
      };
    }

    return blockAndClear({ accountId: payload.accountId });
  };

  return { blockFan, isPending };
}
